import { FC } from 'react'

import {
  BankingPaymentFilter,
  BankingTransferFilter,
  TransfersCountWidgetDocument,
  TransfersCountWidgetQuery,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  FinanceWidget,
  SupportedCharts,
} from 'modules/finance/components/Widgets/common/FinanceWidget'
import { removeNullBetween } from 'modules/finance/components/Widgets/common/utils'
import { buildWidget } from 'modules/finance/components/Widgets/common/widgets'

interface QueryArgsType {
  onlyPending: boolean
  dueThisWeek: boolean
  onTime: boolean
  overdue: boolean
}

const prepareData = (data: TransfersCountWidgetQuery) => {
  const count = data?.banking.transfersConnection.aggregate?.count || 0

  return {
    countersChartDatas: [
      {
        label: i18n.t('finance.overview.outstanding_payments.total'),
        value: count,
        formattedValue: i18n.formatNumber(count),
      },
    ],
  }
}

const buildTransferDataTableFiltersFromPaymentFilters = (
  filters: BankingPaymentFilter
): BankingTransferFilter => {
  const { date: createdAt, dueDate } = filters

  return { createdAt, dueDate }
}

export const buildTransfersCountWidget = ({
  widgetTitle,
  queryArgs,
  paymentFilters,
  transferFilters,
}: {
  widgetTitle?: string
  queryArgs: QueryArgsType
  paymentFilters?: BankingPaymentFilter
  transferFilters?: BankingTransferFilter
}) => {
  const TransfersCountWidget: FC<{
    data: ReturnType<typeof prepareData>
  }> = ({ data }) => {
    const { countersChartDatas } = data

    return (
      <FinanceWidget
        id={`transfersCountDashboard-${widgetTitle}`}
        type={SupportedCharts.COUNTERS}
        showChart={false}
        countersChartDatas={countersChartDatas.map((chartData) => ({
          ...chartData,
          label: widgetTitle || chartData.label,
        }))}
      />
    )
  }

  // This is to avoid problems with date filter and between provoked by the use of datatable filters
  const filteredTransferFilters = paymentFilters
    ? removeNullBetween(buildTransferDataTableFiltersFromPaymentFilters(paymentFilters))
    : removeNullBetween(transferFilters || {})

  return buildWidget({
    id: `transfersCountWidget-${widgetTitle}`,
    component: TransfersCountWidget,
    document: TransfersCountWidgetDocument,
    prepareData: (data: TransfersCountWidgetQuery) => prepareData(data),
    queryArgs: {
      ...queryArgs,
      transfersFilters: {
        ...filteredTransferFilters,
      },
    },
  })
}
